import React from "react";
import "./ClassDetails.css";
import { Button, Row, Col } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle,
  faDownload,
  faVolumeUp,
} from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useTranslation } from "react-i18next";
import Linkify from "react-linkify";

// components
import { Material, topicType, MyTopicType, City } from "types";
import UnskipTopicButton from "pages/class_details/UnskipTopicButton";
import SkipTopicButton from "./SkipTopicButton";

// types

type TopicDetails = topicType & { id: number };

type topicDetailsProps = {
  topic?: TopicDetails;
  myTopic: MyTopicType;
  appointmentNote?: string;
  refresh: () => void;
};

const TopicDetails = ({
  topic,
  myTopic,
  appointmentNote,
  refresh,
}: topicDetailsProps) => {
  const { t } = useTranslation("class_details");

  const renderParagraph = (paragraph: string | undefined) => {
    if (!paragraph) return "";

    const [firstLine, ...rest] = paragraph.split("\n");

    return (
      <p>
        {firstLine}
        {rest.map((line) => (
          <React.Fragment key={line}>
            <br />
            {line}
          </React.Fragment>
        ))}
      </p>
    );
  };

  const renderMaterials = (materials: Material[]) => {
    const materialList = materials
      .slice()
      .sort((a, b) => {
        if (a.city.name < b.city.name) {
          return -1;
        }
        if (a.city.name > b.city.name) {
          return 1;
        }
        return 0;
      })
      .map((e: Material) => (
        <Row key={e.id} justify="start">
          <Col xs={24} sm={12}>
            {e.learningMaterialLink && (
              <Button
                className="section-button size-1 learning-materials"
                name="bannerButton"
                type="primary"
                href={e.learningMaterialLink}
                download
              >
                <FontAwesomeIcon
                  icon={faDownload as IconProp}
                  className="download-icon"
                  size="2x"
                />
                {t("LEARNING MATERIALS")}
                <div className="category">{e.city.name}</div>
              </Button>
            )}
          </Col>

          <Col xs={24} sm={12}>
            {e.audioLink && (
              <Button
                className="section-button size-1 learning-materials"
                name="bannerButton"
                type="primary"
                href={e.audioLink}
                download
              >
                <FontAwesomeIcon
                  icon={faVolumeUp as IconProp}
                  className="download-icon"
                  size="2x"
                />
                {t("AUDIO FILE")}
                <div className="category">{e.city.name}</div>
              </Button>
            )}
          </Col>
        </Row>
      ));

    return (
      <Col
        sm={24}
        md={16}
        className="table-col"
        style={{ flexDirection: "column" }}
      >
        {materialList}
      </Col>
    );
  };

  const renderStudiedOrSkipped = (curTopic: TopicDetails) => {
    if (myTopic.studied) {
      return (
        <Button>
          {t("You studied this")}
          {myTopic.cities?.map((city: City) => (
            <span className="city-name" key={city.name}>
              {city.name}
            </span>
          ))}
        </Button>
      );
    }

    if (myTopic.skipped) {
      return (
        <UnskipTopicButton
          targetTopicId={curTopic.id}
          unskippedSuccessFn={refresh}
        />
      );
    }

    return (
      <SkipTopicButton targetTopicId={curTopic.id} skippedSuccessFn={refresh} />
    );
  };

  const renderTopic = (curTopic: TopicDetails) => {
    return (
      <>
        <Row className="detail-table">
          <Col xs={24} sm={24} md={8} lg={8} xl={8} className="table-col">
            <div className="col-header">{t("INTRODUCTION")}</div>
            <div className="col-body">
              <Linkify
                componentDecorator={(decoratedHref, decoratedText, key) => (
                  <a target="blank" href={decoratedHref} key={key}>
                    {decoratedText}
                  </a>
                )}
              >
                {renderParagraph(curTopic.description)}
              </Linkify>
            </div>
          </Col>
          <Col xs={24} sm={24} md={8} lg={8} xl={8} className="table-col">
            <div className="col-header">{t("STUDY GOALS")}</div>
            <div className="col-body study-goals">
              {curTopic.studyGoals.map((goal: string) => (
                <div className="goal" key={goal}>
                  <FontAwesomeIcon
                    icon={faCircle as IconProp}
                    className="bullet-point"
                    size="2x"
                  />
                  <span>{goal}</span>
                </div>
              ))}
            </div>
          </Col>
          <Col xs={24} sm={24} md={8} lg={8} xl={8} className="table-col">
            <div className="col-header">{t("ADDITIONAL RESOURCES")}</div>
            <div className="col-body additional-resources">
              <Linkify
                componentDecorator={(decoratedHref, decoratedText, key) => (
                  <a target="blank" href={decoratedHref} key={key}>
                    {decoratedText}
                  </a>
                )}
              >
                {renderParagraph(curTopic.additionalResources)}
              </Linkify>
            </div>
          </Col>
        </Row>
        <Row className="table-footer">
          {renderMaterials(curTopic.materials)}
          <Col xs={24} sm={24} md={8} className="table-col bottom-right-area">
            {renderStudiedOrSkipped(curTopic)}
          </Col>
        </Row>
      </>
    );
  };

  return (
    <div className="topic-details section">
      <div className="section-header">
        <span className="section-title">
          {t("TOPIC DETAILS")}
          {": "}
          {topic && topic.title}
        </span>
      </div>
      {appointmentNote ? (
        <div className="section-detail appointment-notes">
          {appointmentNote}
        </div>
      ) : null}

      {topic && renderTopic(topic)}
    </div>
  );
};

export default TopicDetails;
